import { useEffect, useRef, useState } from 'react';
import { Cancel, ClearOutlined, DownloadOutlined } from '@mui/icons-material';
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip
} from '@mui/material';
import { FieldProps } from '@rjsf/core';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

import { FileResource, useFile } from '@attackiq/queries';

const PCAP_MIME_TYPES = ['application/vnd.tcpdump.pcap', 'application/x-pcapng'];
const ACCEPTED_EXTENSIONS = '.pcap,.pcapng';
const MAX_FILE_SIZE_HINT = 'max pcap file size 50MB';
const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 60000;

const DEFAULT_PARSE_ERROR = 'There has been an error while parsing the PCAP file provided';
const INVALID_MIME_ERROR = 'Please provide a valid PCAP file.';
const PARSE_ERROR_BY_CODE: Record<number, string> = {
  1: DEFAULT_PARSE_ERROR,
  2: 'The given PCAP file should contain a TCP conversation between two Assets'
};

interface PcapUploadOptions {
  validationMessage?: string;
}

export const PcapUploadField = (props: FieldProps) => {
  const { autofocus, disabled, formData, name, rawErrors, required, schema, uiSchema, onChange } = props;
  const { validationMessage } = (uiSchema?.['ui:options'] ?? {}) as PcapUploadOptions;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [localError, setLocalError] = useState<string | null>(null);
  const [pollingFileId, setPollingFileId] = useState<string | null>(null);
  const [pendingFileName, setPendingFileName] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const currentValue = typeof formData === 'string' && formData ? formData : null;
  const currentDisplayName = currentValue ? extractDisplayName(currentValue) : null;
  const isProcessing = !!pollingFileId;

  const { mutateAsync: uploadFile, isPending: isUploading } = useMutation({
    mutationFn: async ({ file, signal }: { file: File; signal: AbortSignal }) => {
      const formDataBody = new FormData();
      formDataBody.append('file', file);

      const { data } = await axios.post<FileResource & { id: string }>('/v1/files', formDataBody, {
        signal,
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-CSRFToken': window?.['csrfToken']
        }
      });

      return data;
    }
  });

  const { data: polledFile } = useFile(pollingFileId ?? '', {
    enabled: !!pollingFileId,
    refetchInterval: pollingFileId ? POLL_INTERVAL_MS : false
  });

  useEffect(() => {
    if (!pollingFileId) return undefined;

    pollTimeoutRef.current = setTimeout(() => {
      setLocalError(DEFAULT_PARSE_ERROR);
      setPollingFileId(null);
      setPendingFileName(null);
    }, POLL_TIMEOUT_MS);

    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
  }, [pollingFileId]);

  /* eslint-disable react-hooks/set-state-in-effect -- TanStack Query v5 removed onSuccess/onError; reacting to terminal poll status requires setState in an effect. */
  useEffect(() => {
    if (!pollingFileId || !polledFile?.meta_data?.status) return;

    if (polledFile.meta_data.status === 'DONE') {
      const fileName = getFileNameFromUrl(polledFile.file);
      onChange(`${pollingFileId}/${fileName}`);
      setPollingFileId(null);
      setPendingFileName(null);
      setLocalError(null);
    } else if (polledFile.meta_data.status === 'ERRORED') {
      const code = polledFile.meta_data.error_code;
      setLocalError((code && PARSE_ERROR_BY_CODE[code]) || DEFAULT_PARSE_ERROR);
      setPollingFileId(null);
      setPendingFileName(null);
    }
  }, [polledFile, pollingFileId, onChange]);
  /* eslint-enable react-hooks/set-state-in-effect */

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (file: File) => {
    setLocalError(null);
    setPendingFileName(file.name);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const uploaded = await uploadFile({ file, signal: controller.signal });
      abortControllerRef.current = null;

      const mime = uploaded.meta_data?.mime_type;
      if (!mime || !PCAP_MIME_TYPES.includes(mime)) {
        setLocalError(INVALID_MIME_ERROR);
        setPendingFileName(null);
        return;
      }

      setPollingFileId(uploaded.id);
    } catch (error) {
      abortControllerRef.current = null;
      if (axios.isCancel(error) || (error as Error)?.name === 'CanceledError') return;
      setLocalError(DEFAULT_PARSE_ERROR);
      setPendingFileName(null);
    }
  };

  const handleDelete = () => {
    onChange(null);
    setLocalError(null);
    setConfirmDeleteOpen(false);
  };

  const handleCancel = () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setPollingFileId(null);
    setPendingFileName(null);
  };

  const hasRawError = !!rawErrors?.length;
  const hasError = !!localError || (!currentValue && hasRawError);
  const helperText = localError ?? (hasRawError ? validationMessage ?? rawErrors?.[0] : MAX_FILE_SIZE_HINT);

  const displayedFiles: DisplayedFile[] = currentValue
    ? [{ id: 'current', label: currentDisplayName ?? currentValue, downloadHref: getDownloadHref(currentValue) }]
    : pendingFileName
    ? [{ id: 'pending', label: pendingFileName }]
    : [];

  return (
    <>
      <Box
        component="input"
        type="file"
        ref={fileInputRef}
        accept={ACCEPTED_EXTENSIONS}
        onChange={async event => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) await handleFileSelected(file);
        }}
        sx={{ display: 'none !important' }}
        hidden
      />

      <Autocomplete<DisplayedFile, false, true, false>
        value={displayedFiles[0] ?? null}
        options={displayedFiles}
        autoFocus={autofocus}
        disabled={disabled || isUploading || isProcessing}
        getOptionLabel={option => option.label}
        renderInput={inputProps => (
          <TextField
            name={name}
            error={hasError}
            required={required}
            label={schema.title}
            helperText={helperText}
            {...inputProps}
            InputProps={{
              ...inputProps.InputProps,
              onClick: () => {
                if (!currentValue && !isUploading && !isProcessing) openFilePicker();
              },
              ...(currentValue && {
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Download" disableInteractive arrow>
                      <IconButton
                        size="small"
                        href={getDownloadHref(currentValue)}
                        target="_blank"
                        onClick={event => event.stopPropagation()}
                        sx={{ p: 0.5 }}
                      >
                        <DownloadOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={!disabled ? 'Clear' : ''} disableInteractive arrow>
                      <span>
                        <IconButton
                          size="small"
                          disabled={disabled}
                          onClick={event => {
                            event.stopPropagation();
                            setConfirmDeleteOpen(true);
                          }}
                          sx={{ p: 0.5 }}
                        >
                          <ClearOutlined fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                )
              }),
              ...((isUploading || isProcessing) && {
                endAdornment: (
                  <InputAdornment position="end" sx={{ gap: 0.5 }}>
                    <CircularProgress color="secondary" size={18} />
                    <Tooltip title="Cancel" disableInteractive arrow>
                      <IconButton
                        size="small"
                        onClick={event => {
                          event.stopPropagation();
                          handleCancel();
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <Cancel fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              })
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, onDelete, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={option.id}
                label={option.label}
                onDelete={() => null}
                deleteIcon={
                  <Box
                    sx={{
                      color: 'unset',
                      display: 'flex',

                      '& svg': theme => ({
                        color: alpha(theme.palette.text.primary, 0.26),

                        '&:hover': {
                          color: alpha(theme.palette.text.primary, 0.4)
                        }
                      })
                    }}
                  >
                    {option.downloadHref && (
                      <Tooltip title="Download" disableInteractive arrow>
                        <DownloadOutlined
                          fontSize="inherit"
                          onClick={() => window.open(option.downloadHref, '_blank')}
                        />
                      </Tooltip>
                    )}
                  </Box>
                }
                {...tagProps}
              />
            );
          })
        }
        PopperComponent={() => null}
        forcePopupIcon={false}
        disableClearable
      />

      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Remove File?</DialogTitle>
        <DialogContent>
          <DialogContentText>Remove this file from the scenario?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface DisplayedFile {
  id: string;
  label: string;
  downloadHref?: string;
}

const getFileNameFromUrl = (filePath: string) => {
  try {
    return new URL(filePath).pathname.split('/').at(-1) ?? '';
  } catch {
    return filePath.split('/').at(-1) ?? '';
  }
};

const extractDisplayName = (modelValue: string) => {
  const fileName = modelValue.split('/').slice(1).join('/');
  try {
    return decodeURIComponent(fileName);
  } catch {
    return fileName;
  }
};

const getDownloadHref = (modelValue: string) => `/downloads/files/${modelValue}`;
